import { useSnapshot } from 'valtio'
import { useMemo, useEffect, useState, useRef } from 'react'
import { appQueryParams, updateQsParam } from '../appParams'
import { miscUiState, openOptionsMenu } from '../globalState'
import { quickDevReconnect } from './AppStatusProvider'
import { indexAtTime, onChangeButtonState, packetsReplayState } from './state/packetsReplayState'
import ReplayPanel from './ReplayPanel'
import PlayerBottomBar from './components/replay/PlayerBottomBar'
import InfoViewPanel from './components/replay/InfoViewPanel'
import TelemetryPanel from './components/replay/TelemetryPanel'
import TopMenu from './components/replay/TopMenu'
import SeekingOverlay from './components/replay/SeekingOverlay'

const EMPTY_PACKETS: any[] = []

export default function PacketsReplayProvider () {
  const state = useSnapshot(packetsReplayState)
  const { gameLoaded } = useSnapshot(miscUiState)

  const [uiProgress, setUiProgress] = useState({ current: 0, total: 0 })

  const seekToTime = (timeMs: number, fallbackPacketIdx?: number) => {
    const totalMs = packetsReplayState.totalTimeMs
    if (!totalMs) return
    const total = packetsReplayState.progress.total
    let index: number
    const exact = indexAtTime(timeMs)
    if (exact !== null) {
      index = exact
    } else if (total > 0) {
      index = Math.round((timeMs / totalMs) * total)
    } else if (fallbackPacketIdx !== undefined) {
      index = fallbackPacketIdx
    } else {
      return
    }
    const clamped = Math.max(0, Math.min(Math.max(total, index), index))
    if (total > 0 && clamped < packetsReplayState.progress.current) {
      packetsReplayState.internalRewindTo = clamped
    } else {
      packetsReplayState.seekTo = clamped
    }
  }

  useEffect(() => {
    const interval = setInterval(() => {
      const next = packetsReplayState.progress
      setUiProgress((prev) => {
        if (prev.current === next.current && prev.total === next.total) return prev
        return { current: next.current, total: next.total }
      })
    }, 50)

    return () => {
      clearInterval(interval)
    }
  }, [])

  const autocomplete = useMemo(() => {
    if (!loadedData) return
    return {
      client: Object.keys(loadedData.protocol.play.toClient.types).filter(a => a.startsWith('packet_')).map(a => a.slice('packet_'.length)),
      server: Object.keys(loadedData.protocol.play.toServer.types).filter(a => a.startsWith('packet_')).map(a => a.slice('packet_'.length))
    }
  }, [gameLoaded])

  if (!state.isOpen) return null

  const mockEventsActive = appQueryParams.mockEvents === '1'
  const demoEvents: typeof state.replayEvents = mockEventsActive && state.totalTimeMs ? [
    { type: 'chat',             time: state.totalTimeMs * 0.08,  packetIndex: 0,  label: 'привет всем!' },
    { type: 'chat',             time: state.totalTimeMs * 0.15,  packetIndex: 1,  label: 'хай' },
    { type: 'high_activity',    time: state.totalTimeMs * 0.22,  packetIndex: 2,  label: 'Активность' },
    { type: 'damage',           time: state.totalTimeMs * 0.27,  packetIndex: 3,  label: 'Урон (3 ❤)' },
    { type: 'damage',           time: state.totalTimeMs * 0.31,  packetIndex: 4,  label: 'Урон (2 ❤)' },
    { type: 'death',            time: state.totalTimeMs * 0.38,  packetIndex: 5,  label: 'Смерть' },
    { type: 'respawn',          time: state.totalTimeMs * 0.42,  packetIndex: 6,  label: 'Возрождение' },
    { type: 'afk',              time: state.totalTimeMs * 0.51,  packetIndex: 7,  label: 'AFK: Player1' },
    { type: 'teleport',         time: state.totalTimeMs * 0.65,  packetIndex: 9,  label: 'Телепорт' },
    { type: 'dimension_change', time: state.totalTimeMs * 0.73,  packetIndex: 10, label: 'Нижний мир' },
    { type: 'high_activity',    time: state.totalTimeMs * 0.82,  packetIndex: 11, label: 'Активность' },
    { type: 'chat',             time: state.totalTimeMs * 0.91,  packetIndex: 12, label: 'gg!' },
  ] as any[] : []

  const packets = state.showPacketList ? state.packetsPlayback : EMPTY_PACKETS
  const activeEvents = mockEventsActive && demoEvents.length ? demoEvents : state.replayEvents

  const handleRestart = () => {
    packetsReplayState.isPlaying = false
    packetsReplayState.seekTo = null
    packetsReplayState.internalRewindTo = 0
  }

  const handleSeek = (index: number) => {
    const clamped = Math.max(0, Math.min(uiProgress.total, index))
    if (clamped < packetsReplayState.progress.current) {
      packetsReplayState.internalRewindTo = clamped
    } else {
      packetsReplayState.seekTo = clamped
    }
  }

  const handleTogglePackets = () => {
    packetsReplayState.isMinimized = false
    packetsReplayState.showPacketList = !packetsReplayState.showPacketList
  }

  return (
    <>
      {state.isSeeking && <SeekingOverlay />}

      {/* Hidden, not unmounted, while seeking: the panels keep their collapsed/scroll state.
          `contents` keeps them laid out exactly as before when shown. */}
      <div style={{ display: state.isSeeking ? 'none' : 'contents' }}>
        <InfoViewPanel
          replayName={state.replayName}
          isPlaying={state.isPlaying}
          progress={uiProgress}
          totalTimeMs={state.totalTimeMs}
          replayEvents={activeEvents as any[]}
          onSeekToTime={seekToTime}
        />

        <TelemetryPanel />

        {!state.showPacketList && (
          <TopMenu
            onTogglePackets={handleTogglePackets}
            onOpenSettings={() => openOptionsMenu('main')}
          />
        )}
      </div>

      <PlayerBottomBar
        isPlaying={state.isPlaying}
        progress={uiProgress}
        speed={state.speed}
        seekTo={typeof state.seekTo === 'number' ? state.seekTo : null}
        replayEvents={activeEvents as any[]}
        totalTimeMs={state.totalTimeMs}
        onPlayPause={(isPlaying) => { packetsReplayState.isPlaying = isPlaying }}
        onSpeedChange={(speed) => {
          packetsReplayState.speed = speed
          updateQsParam('replaySpeed', speed === 1 ? undefined : speed.toString())
        }}
        onSeek={handleSeek}
      />

      {state.showPacketList && !state.isSeeking && (
        <ReplayPanel
          hidePlaybackBar
          style={{
            position: 'fixed',
            top: '44px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '440px',
            maxWidth: 'calc(100vw - 508px)',
            maxHeight: 'min(500px, calc(100dvh - 54px - 60px))',
            zIndex: 110,
            overflowY: 'auto' as const,
          }}
          replayName={state.replayName}
          packets={packets}
          isPlaying={state.isPlaying}
          progress={uiProgress}
          speed={state.speed}
          replayEvents={activeEvents as any[]}
          totalTimeMs={state.totalTimeMs}
          onSeekToTime={seekToTime}
          defaultFilter={appQueryParams.replayFilter ?? ''}
          clientPacketsAutocomplete={autocomplete?.client ?? []}
          serverPacketsAutocomplete={autocomplete?.server ?? []}
          customButtons={state.customButtons}
          onPlayPause={(isPlaying) => { packetsReplayState.isPlaying = isPlaying }}
          onRestart={handleRestart}
          onSpeedChange={(speed) => {
            packetsReplayState.speed = speed
            updateQsParam('replaySpeed', speed === 1 ? undefined : speed.toString())
          }}
          onFilterChange={(filter) => { updateQsParam('replayFilter', filter) }}
          onCustomButtonToggle={(button) => {
            onChangeButtonState(button as keyof typeof packetsReplayState.customButtons, !state.customButtons[button].state)
          }}
        />
      )}
    </>
  )
}
